import { useCallback, useEffect, useRef, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';

const STATUS_QUERY = `query GetRepricerApplyStatus { getRepricerApplyStatus }`;
const APPLY_MUTATION = `mutation ApplyRepricerPrices { applyRepricerPrices }`;

interface ApplyStatus {
  state: 'NEVER_RUN' | 'RUNNING' | 'DONE' | 'ERROR' | 'UNKNOWN';
  started_at?: string | null;
  finished_at?: string | null;
  patched?: number;
  failed?: number;
  skipped?: number;
  matched?: number;
  scanned?: number;
  error?: string | null;
}

function parseField(result: any, field: string): ApplyStatus | null {
  const raw = result?.data?.[field];
  if (!raw) return null;
  try {
    return JSON.parse(raw) as ApplyStatus;
  } catch {
    return null;
  }
}

function fmt(ts?: string | null): string {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d.getTime()) ? ts : d.toLocaleString();
}

function stateChipColor(state?: string): 'success' | 'info' | 'error' | 'default' {
  switch (state) {
    case 'DONE':
      return 'success';
    case 'RUNNING':
      return 'info';
    case 'ERROR':
      return 'error';
    default:
      return 'default';
  }
}

export default function RepricerApplyPanel() {
  const [status, setStatus] = useState<ApplyStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const result: any = await API.graphql(graphqlOperation(STATUS_QUERY));
      const parsed = parseField(result, 'getRepricerApplyStatus');
      if (parsed) setStatus(parsed);
    } catch (e) {
      console.error('Failed to fetch apply status:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load.
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Poll while a run is active so the dashboard reflects live progress.
  useEffect(() => {
    const running = status?.state === 'RUNNING';
    if (running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 15000);
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [status?.state, fetchStatus]);

  const handleApply = useCallback(async () => {
    setConfirmOpen(false);
    setSubmitting(true);
    setError(null);
    try {
      const result: any = await API.graphql(graphqlOperation(APPLY_MUTATION));
      const parsed = parseField(result, 'applyRepricerPrices');
      if (parsed) setStatus(parsed);
      // Kick off polling immediately.
      await fetchStatus();
    } catch (e) {
      console.error('Failed to start re-publish:', e);
      setError(e instanceof Error ? e.message : 'Failed to start re-publish');
    } finally {
      setSubmitting(false);
    }
  }, [fetchStatus]);

  const isRunning = status?.state === 'RUNNING';

  return (
    <Box sx={{ px: 3, mt: 3 }}>
      <Paper elevation={0} sx={{ p: 2.5, border: '1px solid #e0e0e0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              Apply prices to Seller Central
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Re-pushes the current repriced prices from DynamoDB to Amazon. Runs in the
              background (~1 hour at Amazon&apos;s rate limit).
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            disabled={isRunning || submitting}
            onClick={() => setConfirmOpen(true)}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {isRunning ? 'Applying…' : submitting ? 'Starting…' : 'Re-push prices'}
          </Button>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          {loading ? (
            <CircularProgress size={18} />
          ) : (
            <>
              <Chip
                label={status?.state ?? 'NEVER_RUN'}
                size="small"
                color={stateChipColor(status?.state)}
                sx={{ fontWeight: 700 }}
              />
              {isRunning && <CircularProgress size={16} />}
              <Typography variant="body2" color="text.secondary">
                {status?.state === 'NEVER_RUN'
                  ? 'Never applied from this dashboard yet.'
                  : `Last ${isRunning ? 'started' : 'applied'}: ${fmt(
                      isRunning ? status?.started_at : status?.finished_at ?? status?.started_at,
                    )}`}
              </Typography>
            </>
          )}
        </Box>

        {status && status.state !== 'NEVER_RUN' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Pushed: <strong>{status.patched ?? 0}</strong>
            {' · '}Failed: <strong>{status.failed ?? 0}</strong>
            {' · '}Skipped: <strong>{status.skipped ?? 0}</strong>
            {typeof status.matched === 'number' && <> {' · '}Matched: <strong>{status.matched}</strong></>}
          </Typography>
        )}

        {status?.error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Error: {status.error}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Paper>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Re-push prices to Seller Central?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This re-publishes the current prices stored in DynamoDB (the actively-repriced
            set) to Amazon Seller Central via SP-API. It will update live listing prices.
            The job runs in the background and can take about an hour to complete.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleApply} variant="contained" color="primary" autoFocus>
            Yes, re-push
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
